import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { requireAdmin } from "@/lib/admin/require-admin.server";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import type { Json } from "@/integrations/supabase/types";
import type { Product } from "@/lib/products";
import { dbError } from "@/lib/db";
import { pushProductToTiny } from "@/lib/erp/product-sync.server";
import { purchaseShippingLabel } from "@/lib/shipping/melhor-envio-label.server";

export const checkAdminAccess = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(async () => ({ ok: true as const }));

// ---- Produtos ----

export const productInputSchema = z.object({
  name: z.string().min(2),
  slug: z.string().min(2),
  categoryId: z.string().uuid().nullable(),
  price: z.number().nonnegative().nullable(),
  compareAtPrice: z.number().nonnegative().nullable(),
  stockQuantity: z.number().int().nonnegative(),
  sku: z.string().nullable(),
  shortDescription: z.string().nullable(),
  description: z.string().nullable(),
  images: z.array(z.string()),
  tags: z.array(z.string()),
  technicalSpecs: z.record(z.unknown()),
  isActive: z.boolean(),
  isFeatured: z.boolean(),
  isCustomQuote: z.boolean(),
  weightKg: z.number().positive().nullable(),
  heightCm: z.number().positive().nullable(),
  widthCm: z.number().positive().nullable(),
  lengthCm: z.number().positive().nullable(),
  ncm: z.string().nullable(),
  gtin: z.string().nullable(),
  unit: z.string().nullable(),
  brand: z.string().nullable(),
});
export type AdminProductInput = z.infer<typeof productInputSchema>;

// Handlers extraídos em funções simples: createServerFn lança "No Start
// context found" quando chamada fora de uma requisição real, então testar a
// const exportada diretamente quebra — mesmo motivo documentado em
// addresses.functions.ts.

// Compartilhada entre insertProduct e editProduct, que gravavam o mesmo
// mapeamento camelCase (schema) → snake_case (coluna) duas vezes.
function toProductFields(data: AdminProductInput) {
  return {
    name: data.name,
    slug: data.slug,
    category_id: data.categoryId,
    price: data.price,
    compare_at_price: data.compareAtPrice,
    stock_quantity: data.stockQuantity,
    sku: data.sku,
    short_description: data.shortDescription,
    description: data.description,
    images: data.images,
    tags: data.tags,
    technical_specs: data.technicalSpecs as Json,
    is_active: data.isActive,
    is_featured: data.isFeatured,
    is_custom_quote: data.isCustomQuote,
    weight_kg: data.weightKg,
    height_cm: data.heightCm,
    width_cm: data.widthCm,
    length_cm: data.lengthCm,
    ncm: data.ncm,
    gtin: data.gtin,
    unit: data.unit,
    brand: data.brand,
  };
}

export async function listAllProducts(): Promise<Product[]> {
  const { data, error } = await supabaseAdmin
    .from("products")
    .select("*, category:categories(id,name,slug,description,sort_order,parent_id)")
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw dbError(error, "src/lib/admin/admin.functions.ts");
  return (data ?? []) as unknown as Product[];
}

export async function getProductById(id: string): Promise<Product | null> {
  const { data, error } = await supabaseAdmin
    .from("products")
    .select("*, category:categories(id,name,slug,description,sort_order,parent_id)")
    .eq("id", id)
    .maybeSingle();
  if (error) throw dbError(error, "src/lib/admin/admin.functions.ts");
  return (data ?? null) as unknown as Product | null;
}

export async function insertProduct(data: AdminProductInput): Promise<{ id: string }> {
  const { data: row, error } = await supabaseAdmin
    .from("products")
    .insert(toProductFields(data))
    .select("id")
    .single();
  if (error) throw dbError(error, "src/lib/admin/admin.functions.ts");

  // Best-effort: uma falha ao criar/vincular no Tiny nunca deve impedir o
  // cadastro do produto na loja — mesmo tratamento dado ao envio de pedidos
  // pagos em mercadopago-webhook.server.ts. Atrás de flag até o payload de
  // criação de produto ser validado contra a API real do Tiny (ver
  // src/lib/erp/tiny-client.server.ts:criarProduto).
  if (process.env.TINY_PRODUCT_PUSH_ENABLED === "true") {
    try {
      await pushProductToTiny(row.id);
    } catch (pushError) {
      console.error(`[tiny] Falha ao criar produto ${row.id} no Tiny`, pushError);
      await supabaseAdmin
        .from("products")
        .update({ tiny_sync_failed_at: new Date().toISOString() })
        .eq("id", row.id);
    }
  }

  return { id: row.id };
}

export async function editProduct(data: AdminProductInput & { id: string }): Promise<void> {
  const { id, ...rest } = data;
  const { error } = await supabaseAdmin.from("products").update(toProductFields(rest)).eq("id", id);
  if (error) throw dbError(error, "src/lib/admin/admin.functions.ts");
}

export async function removeProduct(id: string): Promise<void> {
  const { error } = await supabaseAdmin.from("products").delete().eq("id", id);
  if (error) throw dbError(error, "src/lib/admin/admin.functions.ts");
}

export const adminListProducts = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(() => listAllProducts());

export const adminGetProduct = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .validator((id: string) => z.string().uuid().parse(id))
  .handler(async ({ data: id }) => getProductById(id));

export const adminCreateProduct = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((data: AdminProductInput) => productInputSchema.parse(data))
  .handler(async ({ data }) => insertProduct(data));

export const adminUpdateProduct = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((data: AdminProductInput & { id: string }) =>
    productInputSchema.extend({ id: z.string().uuid() }).parse(data),
  )
  .handler(async ({ data }) => editProduct(data));

export const adminDeleteProduct = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((id: string) => z.string().uuid().parse(id))
  .handler(async ({ data: id }) => removeProduct(id));

// ---- Pedidos ----

export const ORDER_STATUSES = [
  "pending",
  "paid",
  "processing",
  "shipped",
  "delivered",
  "cancelled",
  "refunded",
] as const;

export interface AdminOrderRecord {
  id: string;
  order_number: number;
  customer_name: string;
  customer_email: string;
  status: string;
  total: number;
  created_at: string;
  tiny_sync_failed_at: string | null;
  shipping_service_id: string | null;
  mp_payment_id: string | null;
  mp_refund_failed_at: string | null;
  melhor_envio_tracking_code: string | null;
  melhor_envio_tracking_url: string | null;
  melhor_envio_label_failed_at: string | null;
}

export async function listAllOrders(): Promise<AdminOrderRecord[]> {
  const { data, error } = await supabaseAdmin
    .from("orders")
    .select(
      "id, order_number, customer_name, customer_email, status, total, created_at, tiny_sync_failed_at, shipping_service_id, mp_payment_id, mp_refund_failed_at, melhor_envio_tracking_code, melhor_envio_tracking_url, melhor_envio_label_failed_at",
    )
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw dbError(error, "src/lib/admin/admin.functions.ts");
  return (data ?? []) as AdminOrderRecord[];
}

export async function editOrderStatus(
  id: string,
  status: (typeof ORDER_STATUSES)[number],
): Promise<void> {
  const { error } = await supabaseAdmin.from("orders").update({ status }).eq("id", id);
  if (error) throw dbError(error, "src/lib/admin/admin.functions.ts");
}

export const adminListOrders = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(() => listAllOrders());

export const adminUpdateOrderStatus = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((data: { id: string; status: string }) =>
    z.object({ id: z.string().uuid(), status: z.enum(ORDER_STATUSES) }).parse(data),
  )
  .handler(async ({ data }) => editOrderStatus(data.id, data.status));

export const adminGenerateShippingLabel = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((data: { id: string }) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data }) => purchaseShippingLabel(data.id));

// ---- Query options (client) ----

export const adminProductsQuery = () =>
  queryOptions({ queryKey: ["admin", "products"], queryFn: () => adminListProducts() });

export const adminProductQuery = (id: string) =>
  queryOptions({
    queryKey: ["admin", "product", id],
    queryFn: () => adminGetProduct({ data: id }),
  });

export const adminOrdersQuery = () =>
  queryOptions({ queryKey: ["admin", "orders"], queryFn: () => adminListOrders() });
